import React from "react";
import clsx from "clsx";
import { formatPercent } from "~/utils/number-format";
import { Token } from "~/types";
import { TokenBadge } from "../TokenBadge/TokenBadge";
import { TokenIconSize } from "../TokenIcon";
import { IconStarFilled, IconStarOutline } from "../Iconfont/Iconfont";
import FireSticker from "../LottieSticker/FireSticker";

export type TokenColumn = {
    label: string;
    key: string;
    value: (token: Token) => string;
    change: (token: Token) => number | string;
};

type TokenItemProps = {
    token: Token;
    columns: TokenColumn[];
    deferredKeyword?: string;
    tokenIndex: number;
    onTokenClick: (token: Token) => void;
    isFavoriteAble?: boolean;
    onFavoriteClick?: (token: Token) => void;
    preComponent?: (token: Token) => React.ReactNode;
};

const TokenItem: React.FC<TokenItemProps> = ({
    token,
    tokenIndex,
    columns,
    onTokenClick,
    deferredKeyword: deferedKeyword = "",
    isFavoriteAble,
    onFavoriteClick,
    preComponent,
}) => {
    const [nameColumn, ...restColumns] = columns;
    const hasNameColumn = nameColumn.key === "name";
    const currentColumns = hasNameColumn ? restColumns : columns;
    const nameColumnChange = hasNameColumn ? nameColumn.change(token) : null;

    return (
        <div
            className="flex items-stretch cursor-pointer"
            data-token-chain-id={token.token_base.chain_id}
            data-token-contract={token.token_base.contract}
            onClick={() => onTokenClick(token)}
        >
            <div className="flex-1 w-0 pl-4 flex items-stretch">
                {preComponent && <div>{preComponent(token)}</div>}
                <div className="flex-none py-3.5 mr-3">
                    <TokenBadge
                        size={TokenIconSize.small}
                        tailIconSize={TokenIconSize.mini21}
                        icon={token.token_base.logo}
                        symbol={token.token_base.symbol}
                        tokenIconBorder={true}
                        chainIcon={token.token_base.chain_logo}
                        chainName={token.token_base.chain_name}
                    />
                </div>
                <div className="flex-1 flex flex-col justify-center">
                    <div className="flex flex-col items-start">
                        <div className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center ">
                            <span
                                className="whitespace-nowrap text-ellipsis overflow-hidden max-w-[45vw]"
                                dangerouslySetInnerHTML={{
                                    __html: deferedKeyword
                                        ? token.token_base.symbol
                                              .toUpperCase()
                                              .replace(
                                                  new RegExp(
                                                      deferedKeyword,
                                                      "i"
                                                  ),
                                                  (match) =>
                                                      `<span class='text-brand_1'>${match}</span>`
                                              )
                                        : token.token_base.symbol.toUpperCase(),
                                }}
                            />
                            {hasNameColumn &&
                                "showHot" in nameColumn &&
                                nameColumn.showHot === true &&
                                tokenIndex < 3 && <FireSticker />}
                        </div>
                        {hasNameColumn ? (
                            <div className="flex items-center">
                                <span className="text-text_2 text-direction1 font-Semibold mr-1">
                                    {nameColumn.value(token)}
                                </span>
                                {typeof nameColumnChange !== "number" ? (
                                    <span className="text-body4 text-text_2">
                                        {nameColumn.change(token)}
                                    </span>
                                ) : (
                                    <span
                                        className={clsx(
                                            "font-semibold text-direction1",
                                            (nameColumn.change(
                                                token
                                            ) as number) == 0
                                                ? "!text-text_3"
                                                : (nameColumn.change(
                                                        token
                                                    ) as number) > 0
                                                  ? "!text-text_buy"
                                                  : "!text-text_sell"
                                        )}
                                    >
                                        {formatPercent(
                                            nameColumn.change(token) as number
                                        )}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className="text-text_2 whitespace-nowrap font-semibold text-direction1">
                                {token.token_base.chain_name}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <>
                {currentColumns.map((column, index, list) => {
                    const isLast = index === list.length - 1;
                    const change = column.change(token);
                    const value = column.value(token);
                    const changeNum = Number(change);
                    const changeIsNaN = isNaN(changeNum);
                    return (
                        <div
                            key={index}
                            className={clsx(
                                "flex-1 w-0 flex items-center justify-end",
                                isLast && "pr-4"
                            )}
                        >
                            <div className="flex flex-col items-end justify-center h-full">
                                <span className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center">
                                    {value}
                                </span>
                                {changeIsNaN ? (
                                    <span className="text-body4 text-text_2">
                                        {change}
                                    </span>
                                ) : (
                                    <span
                                        className={clsx(
                                            "font-semibold text-direction1",
                                            changeNum == 0
                                                ? "!text-text_3"
                                                : changeNum > 0
                                                  ? "!text-text_buy"
                                                  : "!text-text_sell"
                                        )}
                                    >
                                        {formatPercent(changeNum)}
                                    </span>
                                )}
                            </div>
                            {isLast && isFavoriteAble && (
                                <div
                                    className="p-2 transform translate-x-2"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onFavoriteClick?.(token);
                                    }}
                                >
                                    {token.token_base.favorite ? (
                                        <IconStarFilled className="text-text_3 text-h5" />
                                    ) : (
                                        <IconStarOutline className="text-text_3 text-h5" />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </>
        </div>
    );
};

export default React.memo(TokenItem);
